import { Tooltip } from '@mui/material';
import { Table, TableBody, TableBodyCell, TableContainer, TableHead, tableHooks } from '@trackman/web-shared-components';
import React, { useMemo } from 'react';
import { CellInfo } from 'react-table';
import { ArchivesTableCell } from '../ArchivesStyles';
import { StyledTableBodyRow, StyledTableHeadCell, StyledTableHeadRow, TableBodyContent } from './GamesTableStyles';
import LoadingOverlay from './LoadingOverlay/LoadingOverlay';

interface IProps {
  data: any[];
  loading: boolean;
}

const GamesTable: React.FC<IProps> = (props) => {
  const { data, loading } = props;

  const renderCell = (info: CellInfo): JSX.Element => {
    return (
      <ArchivesTableCell title={info.value as string} data-test={`${info.column.id}-${info.row.id}`}>
        {info.value as string}
      </ArchivesTableCell>
    );
  };

  const renderTooltipCell = (info: CellInfo): JSX.Element => {
    return (
      <Tooltip title={info.value}>
        <ArchivesTableCell data-test={`${info.column.id}-${info.row.id}`}>{info.value as string}</ArchivesTableCell>
      </Tooltip>
    );
  };

  const { useTable, useSortBy, usePagination, useRowSelect, useResizeColumns, useFlexLayout } = tableHooks;

  const columns = useMemo(
    () => [
      {
        Header: 'Start time',
        accessor: 'startTime',
        Cell: renderCell,
        width: 140,
      },
      {
        Header: 'Away Team',
        accessor: 'awayTeam',
        Cell: renderTooltipCell,
        width: 130,
      },
      { Header: 'Home Team', accessor: 'homeTeam', Cell: renderTooltipCell, width: 130 },
      {
        Header: 'League',
        accessor: 'league',
        Cell: renderTooltipCell,
        width: 115,
      },
      {
        Header: 'Level',
        accessor: 'level',
        Cell: renderTooltipCell,
        width: 115,
      },
      {
        Header: 'Field',
        accessor: 'fieldName',
        Cell: renderTooltipCell,
        width: 100,
      },
      {
        Header: 'Tagged',
        accessor: 'isOperatorlessTaggingEnabled',
        width: 75,
        align: 'center',
      },
      { Header: 'Video Upload', accessor: 'videoUploadStatus', width: 115, align: 'center' },
      { Header: 'Session Status', accessor: 'reviewButton', width: 135, align: 'center' },
    ],
    [data]
  );

  const { getTableProps, rows, headerGroups, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useFlexLayout,
    useResizeColumns
  );

  return (
    <TableContainer>
      <Table {...getTableProps()}>
        <TableHead dark>
          {headerGroups.map(({ headers, getHeaderGroupProps }) => (
            <StyledTableHeadRow {...getHeaderGroupProps()}>
              {headers.map(({ sortable, isSorted, isSortedDesc, align, render, getHeaderProps, getSortByToggleProps, totalWidth }) => {
                return (
                  <StyledTableHeadCell
                    {...getHeaderProps(sortable && getSortByToggleProps())}
                    sortable={sortable}
                    sortDirection={isSorted ? (isSortedDesc ? 'desc' : 'asc') : false}
                    align={align}
                    style={{ ...getHeaderProps().style, maxWidth: totalWidth }}
                  >
                    {render('Header')}
                  </StyledTableHeadCell>
                );
              })}
            </StyledTableHeadRow>
          ))}
        </TableHead>
        <TableBody>
          <TableBodyContent>
            {loading && <LoadingOverlay />}
            {rows.map((row, index) => {
              prepareRow(row);
              return (
                <StyledTableBodyRow index={index} {...row.getRowProps()} data-test={`single-game-row-${index}`}>
                  {row.cells.map(({ getCellProps, column, render }) => (
                    <TableBodyCell
                      {...getCellProps()}
                      align={column.align}
                      style={{ ...getCellProps().style, maxWidth: column.totalWidth }}
                    >
                      {render('Cell')}
                    </TableBodyCell>
                  ))}
                </StyledTableBodyRow>
              );
            })}
          </TableBodyContent>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GamesTable;
